import classNames from 'classnames';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ANY_SPORT_MY_CARDS_BOXES_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';

import { MyCardsRaritySwitcherBox } from '../MyCardsRaritySwitcherBox';

const StyledTabBar = styled(TabBar)`
  margin: 0 var(--unit);
`;

type BoxType = Nullable<
  Exclude<Rarity, typeof Rarity.custom_series> | 'rivals' | 'loyalty'
>;

const getBoxIllustration = (key: BoxType) => {
  if (key === 'rivals') {
    return 'rivals-daily-claim-3';
  }
  if (key === 'loyalty') {
    return 'gold';
  }
  return `${key}-standard`;
};

type Props = {
  boxTypes: readonly (BoxType | null)[];
  unopenedProbabilisticBundlesByRarity: Record<string, number>;
  type: BoxType;
};

export const BoxRaritySwitcher = ({
  unopenedProbabilisticBundlesByRarity,
  boxTypes: boxTypesProps,
  type,
}: Props) => {
  const boxTypes = boxTypesProps.filter(Boolean);
  const { generateSportContextPath } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();

  const renderBoxItem = (key: BoxType) => {
    const boxIllustration = getBoxIllustration(key);
    const src = `${FRONTEND_ASSET_HOST}/football/probabilistic_bundles/${boxIllustration}/picture.png`;
    const count = !!key && unopenedProbabilisticBundlesByRarity?.[key];
    const to = generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES_TYPE, {
      params: {
        type: key || '',
      },
    });
    if (!isLaptopAndAbove) {
      return (
        <Tab key={key} to={to}>
          <ResponsiveImg
            src={src}
            alt=""
            width={16}
            height={16}
            cropWidth={40}
          />
          <LabelL bold>{count}</LabelL>
        </Tab>
      );
    }

    return (
      <MyCardsRaritySwitcherBox
        key={key}
        to={to}
        className={classNames({
          active: type === key,
        })}
      >
        <ResponsiveImg src={src} alt="" width={32} height={32} cropWidth={40} />
        <LabelM bold>{count}</LabelM>
      </MyCardsRaritySwitcherBox>
    );
  };

  if (!isLaptopAndAbove) {
    return (
      <StyledTabBar scrollable>{boxTypes.map(renderBoxItem)}</StyledTabBar>
    );
  }

  return boxTypes.map(renderBoxItem);
};
